import React from 'react';
import { View, Text } from 'react-native';
import Svg, { Polygon, Line, Circle, Text as SvgText, G } from 'react-native-svg';
import { useThemeColors } from '@/lib/useThemeColors';
import { scoreToLabel, capitalizeLabel, labelThemeColor } from '@/lib/scoring/score-display';

interface RadarDimension {
  name: string;
  score: number;
}

interface ScoreRadarChartProps {
  dimensions: RadarDimension[];
  size?: number;
  color?: string; // tint color for the filled polygon
  showLabels?: boolean;
  showScoresInLabels?: boolean;
}

const RING_SCALES = [0.2, 0.4, 0.6, 0.8, 1.0];

const MAX_LINE_CHARS = 11;

/**
 * Wrap a label to ≤2 lines for axis placement.
 * - Multi-word names split on the midpoint space ("Financial Health" → 2 lines)
 * - Single long words (>MAX) split at the midpoint character ("Defensibility" → "Defensi" / "bility")
 * - Multi-word names with a long word kept as-is — the renderer auto-shrinks the long line's
 *   fontSize so the full word stays readable without char-splitting.
 */
function wrapLabel(name: string): string[] {
  const words = name.split(/\s+/);
  if (words.length === 1) {
    if (name.length <= MAX_LINE_CHARS) return [name];
    const mid = Math.ceil(name.length / 2);
    return [name.slice(0, mid), name.slice(mid)];
  }
  if (words.length === 2) return words;
  const mid = Math.ceil(words.length / 2);
  return [words.slice(0, mid).join(' '), words.slice(mid).join(' ')];
}

/**
 * Compute geometry shared by both on-screen and PDF renderers.
 * Returns absolute coordinates for: axes, score polygon, reference rings.
 */
export function computeRadarGeometry(
  dimensions: RadarDimension[],
  size: number,
): {
  cx: number;
  cy: number;
  r: number;
  axes: Array<{
    angle: number;
    tipX: number;
    tipY: number;
    labelX: number;
    labelY: number;
    labelAnchor: 'start' | 'middle' | 'end';
    name: string;
    score: number;
  }>;
  polygonPoints: Array<{ x: number; y: number }>;
  referenceRings: Array<Array<{ x: number; y: number }>>;
} {
  const cx = size / 2;
  const cy = size / 2;
  // Inner chart radius — leaves generous room for axis labels (name + score line).
  // 0.20 ratio + larger labelDist offset = greater angular separation so neighbouring
  // axes' labels don't visually collide on small sizes (~240px) with 7-8 axes.
  const r = size * 0.20;
  const labelDist = r + 18;

  const n = dimensions.length;

  const axes = dimensions.map((d, i) => {
    const angle = (i * 2 * Math.PI) / n - Math.PI / 2;
    const cosA = Math.cos(angle);
    const sinA = Math.sin(angle);
    // Anchor labels based on angle: top/bottom = middle, right side = start, left side = end
    const tolerance = 0.1;
    let labelAnchor: 'start' | 'middle' | 'end' = 'middle';
    if (Math.abs(cosA) > tolerance) {
      labelAnchor = cosA > 0 ? 'start' : 'end';
    }
    return {
      angle,
      tipX: cx + r * cosA,
      tipY: cy + r * sinA,
      labelX: cx + labelDist * cosA,
      labelY: cy + labelDist * sinA,
      labelAnchor,
      name: d.name,
      score: d.score,
    };
  });

  const polygonPoints = axes.map((a) => {
    const t = Math.max(0, Math.min(1, a.score / 100));
    return {
      x: cx + r * t * Math.cos(a.angle),
      y: cy + r * t * Math.sin(a.angle),
    };
  });

  const referenceRings = RING_SCALES.map((scale) =>
    axes.map((a) => ({
      x: cx + r * scale * Math.cos(a.angle),
      y: cy + r * scale * Math.sin(a.angle),
    })),
  );

  return { cx, cy, r, axes, polygonPoints, referenceRings };
}

export function ScoreRadarChart({
  dimensions,
  size = 240,
  color,
  showLabels = true,
  showScoresInLabels = false,
}: ScoreRadarChartProps) {
  const colors = useThemeColors();
  const tint = color ?? colors.primary[500];
  const { cx, cy, axes, polygonPoints, referenceRings } = computeRadarGeometry(dimensions, size);

  if (dimensions.length < 3) {
    return null;
  }

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Reference rings */}
        {referenceRings.map((ring, i) => (
          <Polygon
            key={`ring-${i}`}
            points={ring.map((p) => `${p.x},${p.y}`).join(' ')}
            fill="none"
            stroke={colors.border.light}
            strokeWidth={0.5}
          />
        ))}

        {/* Axis lines */}
        <G>
          {axes.map((a, i) => (
            <Line
              key={`axis-${i}`}
              x1={cx}
              y1={cy}
              x2={a.tipX}
              y2={a.tipY}
              stroke={colors.border.light}
              strokeWidth={0.5}
            />
          ))}
        </G>

        {/* Filled score polygon */}
        <Polygon
          points={polygonPoints.map((p) => `${p.x},${p.y}`).join(' ')}
          fill={`${tint}33`}
          stroke={tint}
          strokeWidth={1.5}
        />

        {/* Score dots */}
        {polygonPoints.map((p, i) => (
          <Circle key={`dot-${i}`} cx={p.x} cy={p.y} r={3} fill={tint} />
        ))}

        {/* Axis labels — name on line 1, "Label (score)" on line 2 */}
        {showLabels &&
          axes.map((a, i) => {
            const qualLabel = a.score > 0 ? capitalizeLabel(scoreToLabel(a.score)) : '—';
            const qualColor = a.score > 0
              ? labelThemeColor(scoreToLabel(a.score), colors)
              : colors.text.tertiary;
            // Wrap long names onto two lines (handles multi-word and single long words)
            const nameParts = a.name.length > 11 ? wrapLabel(a.name) : [a.name];
            // Vertical layout: y is the baseline of each line (no alignmentBaseline,
            // since it's unreliable in react-native-svg on web).
            // Place the whole label block centered on a.labelY.
            const NAME_FS = 10;
            const SCORE_FS = 9.5;
            const LINE_GAP = 3;
            const lineHeight = NAME_FS + 2;
            const scoreLineHeight = showScoresInLabels ? SCORE_FS + 2 + LINE_GAP : 0;
            const blockHeight = nameParts.length * lineHeight + scoreLineHeight;
            // first line baseline: top of block + ascent (≈ fontSize)
            const firstBaseline = a.labelY - blockHeight / 2 + NAME_FS;
            return (
              <G key={`lbl-${i}`}>
                {nameParts.map((part, idx) => (
                  <SvgText
                    key={`np-${idx}`}
                    x={a.labelX}
                    y={firstBaseline + idx * lineHeight}
                    fontSize={NAME_FS}
                    fontWeight="700"
                    textAnchor={a.labelAnchor}
                    fill={colors.text.primary}
                  >
                    {part}
                  </SvgText>
                ))}
                {showScoresInLabels && (
                  <SvgText
                    x={a.labelX}
                    y={firstBaseline + nameParts.length * lineHeight + LINE_GAP}
                    fontSize={SCORE_FS}
                    fontWeight="700"
                    textAnchor={a.labelAnchor}
                    fill={qualColor}
                  >
                    {`${a.score}  ${qualLabel}`}
                  </SvgText>
                )}
              </G>
            );
          })}
      </Svg>
    </View>
  );
}
